import { escapeHtml } from "./pdfUtils";
import { getTarotCardArt } from "../tarot/art";

const ACCENT = "#5ef0d8";
const LAVENDER = "#7fb4ff";

export function buildTarotReadingPdfHtml({ cards, synthesisText, exportedAt, labels = {} }) {
  const l = labels;
  return `
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&family=DM+Mono:wght@300;400&display=swap" rel="stylesheet">
    <style>
      @page { size: A4; margin: 12mm; }
      * { box-sizing: border-box; }
      body {
        font-family: "DM Sans", "Inter", "Segoe UI", Arial, sans-serif;
        margin: 0;
        color: #e8ebff;
        background: #050816;
        font-weight: 300;
      }
      .sheet {
        position: relative;
        border: 1px solid rgba(94,240,216,0.20);
        border-radius: 18px;
        overflow: hidden;
        background:
          radial-gradient(ellipse 70% 40% at 20% 100%, rgba(127,180,255,0.10) 0%, transparent 60%),
          radial-gradient(circle at top right, #172148, #090d22 56%, #050816);
      }
      .sheet::before {
        content: "";
        position: absolute;
        inset: 0;
        background-image:
          radial-gradient(1px 1px at 10% 15%, rgba(255,255,255,0.45) 0%, transparent 100%),
          radial-gradient(1px 1px at 25% 60%, rgba(255,255,255,0.30) 0%, transparent 100%),
          radial-gradient(1.2px 1.2px at 40% 30%, rgba(255,255,255,0.40) 0%, transparent 100%),
          radial-gradient(1px 1px at 55% 80%, rgba(255,255,255,0.25) 0%, transparent 100%),
          radial-gradient(1px 1px at 70% 20%, rgba(255,255,255,0.40) 0%, transparent 100%),
          radial-gradient(1.2px 1.2px at 80% 55%, rgba(255,255,255,0.30) 0%, transparent 100%);
        pointer-events: none;
        z-index: 0;
      }
      .sheet > * { position: relative; z-index: 1; }
      .header {
        padding: 26px 28px 18px;
        border-bottom: 1px solid rgba(94,240,216,0.20);
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 12px;
      }
      .eyebrow {
        display: inline-block;
        font-family: "Playfair Display", "Georgia", serif;
        font-size: 24px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: ${ACCENT};
        margin-bottom: 8px;
      }
      .meta {
        color: #8a8ea8;
        font-size: 11px;
        font-family: "DM Mono", monospace;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        text-align: right;
      }
      .content { padding: 20px 24px 24px; }
      .cards-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 14px; }
      .card {
        position: relative;
        overflow: hidden;
        border: 1px solid rgba(94,240,216,0.20);
        border-radius: 12px;
        padding: 14px 14px 12px;
        background: rgba(19,17,42,0.85);
      }
      .card::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 2px;
        height: 100%;
        background: linear-gradient(to bottom, ${ACCENT}, transparent);
        opacity: 0.6;
      }
      .card-badge {
        display: inline-block;
        font-size: 9px;
        text-transform: uppercase;
        letter-spacing: 0.2em;
        color: ${ACCENT};
        border: 1px solid rgba(94,240,216,0.30);
        border-radius: 999px;
        padding: 4px 8px;
        margin-bottom: 10px;
        font-family: "DM Mono", monospace;
      }
      .card-art {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 140px;
        margin: 4px 0 10px;
      }
      .card-art svg {
        width: 140px;
        height: 140px;
        display: block;
      }
      .card-title {
        font-size: 22px;
        line-height: 1.15;
        margin-bottom: 8px;
        color: #ffffff;
        font-family: "Playfair Display", "Georgia", serif;
      }
      .card-num {
        font-size: 10px;
        color: ${LAVENDER};
        letter-spacing: 0.12em;
        margin-bottom: 8px;
        font-family: "DM Mono", monospace;
        text-transform: uppercase;
      }
      .card-meaning { font-size: 12px; line-height: 1.65; color: #d2dcff; }
      .synthesis {
        margin-top: 16px;
        padding: 16px;
        border: 1px solid rgba(94,240,216,0.35);
        border-radius: 12px;
        background: linear-gradient(180deg, rgba(94,240,216,0.10), rgba(94,240,216,0.04));
      }
      .synthesis-title {
        font-size: 10px;
        font-family: "DM Mono", monospace;
        letter-spacing: 0.3em;
        margin-bottom: 8px;
        color: ${ACCENT};
        text-transform: uppercase;
      }
      .synthesis-text { font-size: 13px; line-height: 1.7; color: #d8f5ee; }
      .footer {
        margin-top: 16px;
        padding-top: 12px;
        border-top: 1px dashed rgba(94,240,216,0.20);
        font-size: 10px;
        color: #8a8ea8;
        font-family: "DM Mono", monospace;
        letter-spacing: 0.12em;
        display: flex;
        justify-content: space-between;
      }
      @media print {
        html, body {
          background: #050816 !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .sheet {
          border: none;
          border-radius: 0;
          background: #050816 !important;
        }
        .sheet::before { display: none !important; }
        .card { background: #13112a !important; }
      }
    </style>
    <main class="sheet">
      <header class="header">
        <div>
          <div class="eyebrow">${escapeHtml(l.report)}</div>
          <div style="font-size:10px; color:#8fa3d0; letter-spacing:0.2em; text-transform:uppercase; font-family:'DM Mono', monospace;">Tarot Reading</div>
        </div>
        <div class="meta">${escapeHtml(l.spreadMeta)} · ${escapeHtml(exportedAt)}</div>
      </header>
      <section class="content">
        <div class="cards-grid">
        ${cards.map((card) => `
          <div class="card">
            <div class="card-badge">${escapeHtml(card.positionLabel || card.pos || "")}</div>
            <div class="card-art">
              <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
                ${getTarotCardArt(card)}
              </svg>
            </div>
            <div class="card-title">${escapeHtml(card.name)}</div>
            <div class="card-num">${escapeHtml(card.number || card.num || "")}${card.reversed ? ` · ${escapeHtml(l.reversed || "Reversed")}` : ` · ${escapeHtml(l.upright || "Upright")}`}</div>
            <div class="card-meaning">${escapeHtml(card.meaning)}</div>
          </div>
        `).join("")}
        </div>
        <div class="synthesis">
          <div class="synthesis-title">${escapeHtml(l.synthesis)}</div>
          <div class="synthesis-text">${escapeHtml(synthesisText)}</div>
        </div>
        <div class="footer">
          <span>${escapeHtml(l.generatedBy)}</span>
          <span>${escapeHtml(exportedAt)}</span>
        </div>
      </section>
    </main>
  `;
}
